"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { useTranslations } from "next-intl";
import { MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { LoadingSpinner } from "@/components/ui/loading";
import { useSearchComposer, type ComposerField } from "./useSearchComposer";
// 패널은 지연 로드(공용 번들 제외) — 유휴 시간에 미리 받는다. lazy-panels.tsx 참고.
import { DatePanel, LocationPanel, WhatPanel, preloadSearchPanels } from "./lazy-panels";

/** 칸별 드롭다운 폭(px) — 뷰포트가 좁으면 좌우 16px 안으로 줄인다 */
const PANEL_WIDTH: Record<ComposerField, number> = { location: 460, date: 860, what: 600 };

/**
 * 데스크톱 검색바 — [위치 | 날짜 | 무엇을 | 검색]. 칸을 누르면 바 전체가 회색으로
 * 가라앉고 그 칸만 흰색으로 떠오르며, 아래에 그 칸의 드롭다운이 열린다(에어비앤비와 같은
 * 동작). 드롭다운은 fixed — 내비 안의 좁은 바 폭에 갇히지 않게 바 좌표로 놓는다.
 */
export function DesktopSearchBar({ compact = false }: { compact?: boolean }) {
  const t = useTranslations("searchComposer");
  const api = useSearchComposer();
  const [open, setOpen] = useState<ComposerField | null>(null);
  const [pos, setPos] = useState<CSSProperties>({});
  const wrapRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => preloadSearchPanels(), []);

  const place = (field: ComposerField) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const vw = window.innerWidth;
    const width = Math.min(PANEL_WIDTH[field], vw - 32);
    const want =
      field === "location"
        ? rect.left
        : field === "what"
          ? rect.right - width
          : rect.left + rect.width / 2 - width / 2;
    setPos({ top: rect.bottom + 12, left: Math.max(16, Math.min(want, vw - width - 16)), width });
  };

  const openField = (field: ComposerField) => {
    // 닫혀 있다 열 때만 URL 로 초안을 다시 채운다 — 칸 사이를 옮겨 다닐 땐 입력을 유지.
    if (!open) api.reset();
    place(field);
    setOpen(field);
  };

  const close = () => setOpen(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      // 드롭다운이 띄운 시트(전체 카테고리)는 포털이라 바깥으로 잡힌다 — 그 안 클릭은 무시
      if (target instanceof Element && target.closest('[role="dialog"], [data-vaul-drawer]')) return;
      setOpen(null);
    };
    // 시트가 열려 있으면 Esc 는 시트만 닫는다
    const onKey = (e: KeyboardEvent) =>
      e.key === "Escape" && !document.querySelector('[role="dialog"][data-state="open"]') && setOpen(null);
    const onResize = () => setOpen(null);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onResize);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onResize);
    };
  }, [open]);

  const submit = async () => {
    if (await api.submit()) close();
  };

  const segments: { field: ComposerField; label: string; value: string | null; empty: string; clear: () => void }[] = [
    {
      field: "location",
      label: t("location"),
      value: api.locationLabel(api.draft.location),
      empty: t("locationEmpty"),
      clear: () => api.patch({ location: { kind: "none" } }),
    },
    {
      field: "date",
      label: t("date"),
      value: api.dateLabel,
      empty: t("dateEmpty"),
      clear: () => api.patch({ checkin: null, checkout: null }),
    },
    {
      field: "what",
      label: t("what"),
      value: api.whatLabel,
      empty: t("whatEmpty"),
      clear: () => api.patch({ categories: [], keyword: "" }),
    },
  ];
  const activeIndex = segments.findIndex((s) => s.field === open);

  /*
   * 컴팩트(09-28, 메인 외 페이지) — 닫혀 있을 땐 한 줄 알약(에어비앤비 "수영 근처의 숙소 |
   * 일주일 아무 때나 | 게스트 추가"). 칸을 누르면 그 칸이 열린 큰 검색바로 펼쳐진다.
   * 래퍼 높이(h-16)를 큰 바와 같게 둬 드롭다운 위치 계산(place)이 두 상태에서 같다.
   */
  if (compact && !open) {
    const compactEmpty: Record<ComposerField, string> = {
      // 빈 칸 문구 = 같은 리듬의 짧은 짝(09-29): 어디서든 · 언제든 · 무엇이든
      location: t("compactWhere"),
      date: t("compactWhen"),
      what: t("compactWhat"),
    };
    return (
      <div ref={wrapRef} className="relative flex h-16 w-full items-center justify-center">
        <div
          role="search"
          className="flex h-12 min-w-0 items-center rounded-full border border-gray-200 bg-white pl-2 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_4px_12px_rgba(0,0,0,0.05)] transition-shadow hover:shadow-[0_2px_4px_rgba(0,0,0,0.12),0_6px_16px_rgba(0,0,0,0.08)]"
        >
          {segments.map((s, i) => (
            <button
              key={s.field}
              type="button"
              onClick={() => openField(s.field)}
              className={cn(
                "relative h-full min-w-0 max-w-56 truncate px-4 text-sm",
                s.value ? "font-medium text-gray-900" : "text-gray-500",
                i > 0 &&
                  "before:absolute before:left-0 before:top-3 before:bottom-3 before:w-px before:bg-gray-300",
              )}
            >
              {s.value ?? compactEmpty[s.field]}
            </button>
          ))}
          <button
            type="button"
            onClick={() => openField("location")}
            aria-label={t("search")}
            className="ml-1 mr-1.5 flex size-8.5 shrink-0 items-center justify-center rounded-full bg-orange-500 text-white hover:bg-orange-600"
          >
            <MagnifyingGlassIcon className="size-4" weight="bold" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative w-full">
      <div
        role="search"
        className={cn(
          "flex h-16 items-center rounded-full border border-gray-200 shadow-[0_3px_12px_rgba(0,0,0,0.08)] transition-colors",
          open ? "bg-gray-100" : "bg-white",
        )}
      >
        {segments.map((s, i) => {
          const on = open === s.field;
          // 구분선은 떠오른 칸의 양옆에서 숨긴다.
          const divider = i > 0 && activeIndex !== i && activeIndex !== i - 1;
          return (
            <div
              key={s.field}
              className={cn(
                "relative flex h-full min-w-0 flex-1 items-center rounded-full",
                on && "bg-white shadow-[0_4px_14px_rgba(0,0,0,0.12)]",
                !on && open && "hover:bg-gray-200",
                !on && !open && "hover:bg-gray-50",
                i === 2 && "flex-[1.3]",
              )}
            >
              {divider && <span aria-hidden className="absolute left-0 top-4 bottom-4 w-px bg-gray-300" />}
              <button
                type="button"
                aria-expanded={on}
                onClick={() => (on ? close() : openField(s.field))}
                className="flex h-full min-w-0 flex-1 flex-col justify-center rounded-full pl-6 pr-2 text-left lg:pl-7"
              >
                <span className="text-xs font-semibold text-gray-900">{s.label}</span>
                <span className={cn("truncate text-sm", s.value ? "font-medium text-gray-900" : "text-gray-500")}>
                  {s.value ?? s.empty}
                </span>
              </button>
              {on && s.value && (
                <button
                  type="button"
                  tabIndex={-1}
                  aria-label={t("clear")}
                  onClick={s.clear}
                  className="mr-2 flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300"
                >
                  <XIcon className="size-3" weight="bold" />
                </button>
              )}
              {i === 2 && (
                <button
                  type="button"
                  onClick={submit}
                  disabled={api.submitting}
                  aria-label={t("search")}
                  className={cn(
                    "mr-2 flex h-12 min-w-12 shrink-0 items-center justify-center gap-1.5 rounded-full bg-orange-500 text-white transition-all hover:bg-orange-600",
                    open ? "px-4" : "px-0",
                  )}
                >
                  {api.submitting ? (
                    <LoadingSpinner size="sm" className="size-4 text-white" />
                  ) : (
                    <MagnifyingGlassIcon className="size-4.5" weight="bold" />
                  )}
                  {open && <span className="text-[15px] font-semibold">{t("search")}</span>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {open && (
        <div
          ref={panelRef}
          style={{ ...pos, position: "fixed" }}
          className="z-60 max-h-[calc(100dvh-7rem)] overflow-y-auto rounded-[28px] bg-white p-6 shadow-[0_12px_40px_rgba(20,18,12,0.16)] ring-1 ring-gray-900/5"
        >
          {open === "location" && (
            <LocationPanel
              api={api}
              onPicked={() => openField("date")}
              onRecent={(href) => {
                close();
                api.openRecent(href);
              }}
            />
          )}
          {open === "date" && <DatePanel api={api} />}
          {open === "what" && <WhatPanel api={api} onNavigate={close} onSubmit={submit} />}
        </div>
      )}
    </div>
  );
}
